<script setup lang="ts">
import { computed, ref } from "vue";
import { useGetStorageInfo } from "@/composable/useStorageQuota";
import { LOCAL_DRAWINGS_LIMIT } from "@/utils/constants";
import $ls, { LocalStorageKey } from "@/utils/local-storage";
import { useTranslation } from "i18next-vue";

const { t } = useTranslation();

const { localDrawingsCount, percentageUsed, percentageLimitLevel } =
  useGetStorageInfo();

function getTodayDateString(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(now.getDate()).padStart(2, "0")}`;
}

function isDismissedToday(): boolean {
  const dismissedDate = $ls.getItem(LocalStorageKey.STORAGE_BANNER_DISMISSED);
  return dismissedDate === getTodayDateString();
}

const isDismissed = ref(isDismissedToday());

const isQuotaHigh = computed(() => percentageLimitLevel.value === "high");
const isDrawingsCountHigh = computed(
  () => localDrawingsCount.value >= LOCAL_DRAWINGS_LIMIT
);

const showBanner = computed(() => {
  return (isQuotaHigh.value || isDrawingsCountHigh.value) && !isDismissed.value;
});

function dismiss() {
  isDismissed.value = true;
  $ls.setItem(LocalStorageKey.STORAGE_BANNER_DISMISSED, getTodayDateString());
}
</script>

<template>
  <div v-if="showBanner" class="storage-banner">
    <div class="banner-content">
      <div class="banner-icon">
        <i class="bi bi-hdd-fill"></i>
      </div>
      <div class="banner-text">
        <strong>{{ t("dashboard:sync.page.storageBanner.title") }}</strong>
        <span v-if="isQuotaHigh">{{
          t("dashboard:sync.page.storageBanner.messageQuota", {
            percent: Math.round(percentageUsed),
          })
        }}</span>
        <span v-else>{{
          t("dashboard:sync.page.storageBanner.message", {
            count: localDrawingsCount,
          })
        }}</span>
      </div>
    </div>
    <a
      class="banner-action"
      href="#offcanvasSettings"
      data-bs-toggle="offcanvas"
      role="button"
      >{{ t("dashboard:sync.page.storageBanner.action") }}</a
    >
    <button class="banner-close" @click="dismiss" aria-label="Close">
      <i class="bi bi-x-lg"></i>
    </button>
  </div>
</template>

<style scoped>
.storage-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background-color: var(--bs-warning-bg-subtle);
  border-bottom: 2px solid var(--bs-warning);
}

.banner-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
}

.banner-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background-color: var(--bs-warning-border-subtle);
  color: var(--bs-warning-text-emphasis);
  flex-shrink: 0;
}

.banner-icon i {
  font-size: 1.2rem;
}

.banner-text {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
}

.banner-text strong {
  color: var(--bs-warning-text-emphasis);
  font-size: 0.9rem;
}

.banner-text span {
  color: var(--bs-body-color);
  font-size: 0.8rem;
  opacity: 0.85;
}

.banner-action {
  color: var(--bs-warning-text-emphasis);
  font-size: 0.85rem;
  font-weight: 600;
  text-decoration: underline;
  white-space: nowrap;
  flex-shrink: 0;
  cursor: pointer;
}

.banner-close {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  color: var(--bs-warning-text-emphasis);
  opacity: 0.7;
  transition: opacity 0.2s;
  flex-shrink: 0;
}

.banner-close:hover {
  opacity: 1;
}

/* Responsive */
@media (max-width: 480px) {
  .storage-banner {
    padding: 0.5rem 0.75rem;
  }

  .banner-icon {
    width: 2rem;
    height: 2rem;
  }

  .banner-icon i {
    font-size: 1rem;
  }

  .banner-text strong {
    font-size: 0.85rem;
  }

  .banner-text span {
    font-size: 0.75rem;
  }
}
</style>
